import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { canModifyContent } from '@/lib/account-roles';
import {
  useAllVendorsQuery,
  useSaveVendorMutation,
  useVendorCategoriesQuery,
} from '@/queries/vendors';
import { useAuthStore } from '@/stores/auth-store';
import {
  HOME_TABS,
  useHomeDashboardStore,
} from '@/stores/home-dashboard-store';
import { FestivalHeader, Loading } from '../controls';
import { VendorForm } from './VendorForm';

export function VendorFormPage(props) {
  const history = useHistory();
  const formRef = useRef(null);
  const currentUser = useAuthStore((s) => s.currentUser);
  const canModify = canModifyContent(currentUser);
  const setActiveTab = useHomeDashboardStore((s) => s.setActiveTab);

  const id = props.match?.params?.id;
  const numericId =
    id !== undefined && id !== '' && !Number.isNaN(Number(id))
      ? String(id)
      : undefined;
  const isNew = !numericId;

  const vendorsQ = useAllVendorsQuery();
  const categoriesQ = useVendorCategoriesQuery();
  const saveMutation = useSaveVendorMutation();

  const loadedVendor = useMemo(() => {
    if (!numericId) {
      return null;
    }
    const list = vendorsQ.data ?? [];
    return list.find((v) => String(v.vendorId) === String(numericId)) ?? null;
  }, [vendorsQ.data, numericId]);

  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    setOverrides({});
  }, [numericId, isNew]);

  const vendor = isNew ? overrides : { ...(loadedVendor ?? {}), ...overrides };

  const onFileUpload = (fileInfo) => {
    const { url, filename } = fileInfo;
    setOverrides((prev) => ({
      ...prev,
      photoURL: url,
      vendorPhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setOverrides((prev) => ({
      ...prev,
      photoURL: null,
      vendorPhoto: null,
    }));
  };

  const handleSave = async (values) => {
    if (!canModify) {
      return;
    }
    try {
      const saved = await saveMutation.mutateAsync({ ...vendor, ...values });
      const savedId = saved?.vendorId ?? vendor.vendorId ?? numericId;
      if (savedId) {
        history.push(`/vendor/${savedId}/view`);
      } else {
        setActiveTab(HOME_TABS.vendors);
        history.push('/');
      }
    } catch {
      /* mutation toast */
    }
  };

  if (!canModify) {
    if (numericId) {
      return <Redirect to={`/vendor/${numericId}/view`} />;
    }
    return <Redirect to="/" />;
  }

  if (!isNew && vendorsQ.isPending) {
    return <Loading />;
  }

  if (!isNew && (vendorsQ.isError || !loadedVendor)) {
    return (
      <div className="py-16 text-center text-red-600">
        Could not load this vendor.
      </div>
    );
  }

  const headerTitle = isNew ? 'Add Vendor' : vendor.vendorName || 'Edit Vendor';

  return (
    <div>
      <FestivalHeader
        title={headerTitle}
        showSave
        showExit
        onSave={() => formRef.current?.requestSubmit()}
        onExit={() => setActiveTab(HOME_TABS.vendors)}
      />
      <div className="w-full">
        <div className="event-main-info min-w-0">
          <VendorForm
            formRef={formRef}
            className="admin-form"
            vendor={vendor}
            vendorCategories={categoriesQ.data ?? {}}
            handleSave={handleSave}
            onFileUpload={onFileUpload}
            onFileDelete={onFileDelete}
            isPending={saveMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
}
